// RemoteDakNodes — Erdotex: de PV-omvormers die andere Pi's uitlezen
// (`snapshot.remote_pv`), per dak als eigen bol-met-pil in de energieflow,
// met een flowlijn naar de Grid-bol.
//
// Alleen gerenderd als de snapshot `remote_pv` met omvormers draagt. Zonder
// remote_pv (VJ/NS/HO) verandert er niets aan FlowSVG.
//
// Plaatsing (Bram 15-10-2026: "vooral horizontaal ruimte, niet verticaal";
// Grid en PV blijven de bovenste rij). De daken krijgen een vierde kolom
// rechts van Grid/Verbruik, op hetzelfde rijraster (cy 75 en 290) en met
// dezelfde pilmaat (280 x 90, rx 45, pil begint op cx). De viewBox groeit
// daarvoor alleen naar rechts.
//
// Lijnen volgen de regels van FlowSVG: anker op de bolrand, cubic bezier met
// controlepunten radiaal buiten het anker, zodat de lijn loodrecht op de rand
// vertrekt en aankomt. Grid heeft rechts zijn pil; het enige vrije stuk rand
// aan de rechterkant ligt tussen 69 en 90 graden (daarboven valt het anker in
// de pil). De dakenlijnen komen daarom van onderen binnen: Dak 1 op 70
// graden, Dak 2 op 93 graden, en de lijn van de accu-omvormer schuift op
// Erdotex naar 122 graden, zodat de drie lijnen als een waaier binnenkomen en
// elkaar niet kruisen.

import { fmtKw, fmtKwBol, fmtKwh } from '../../lib/format';
import { SunIcon } from './icons';
import type { RemotePv } from '../../api/types';

type Circle = { cx: number; cy: number; r: number };

/** Grid-anker van de lijn accu-omvormer → Grid als er daken zijn. */
export const GRID_ANCHOR_INV_MET_DAKEN = 122;

/** Desktop: viewBox-breedte met de dakenkolom (pil eindigt op 1140, marge 20 zoals rechts op 780/800). */
export const VIEWBOX_BREEDTE_MET_DAKEN = 1200;

// Bovenste rij blijft PV en Grid; de daken staan op de rijen van Verbruik
// en Backup, in een vierde kolom (bol op cx=900, pil 900..1180).
const DESKTOP_POS = [
  { cx: 900, cy: 290 },  // rij van Verbruik
  { cx: 900, cy: 505 },  // rij van Backup
];
const R = 32;

// Per dak: vertrekhoek op de dakbol, aankomsthoek op Grid, controle-afstanden.
// Desktop. Dak 1 vertrekt linksboven (225) en komt op 70 graden binnen.
// Dak 2 kan niet in één bezier naar Grid zonder door de Verbruik-pil te
// snijden: hij buigt eerst met een kwartboog (dezelfde kromming) naar een
// verticale baan tussen de Verbruik-pil (eindigt op 780) en Dak 1, en komt
// vandaar als binnenste lijn op 95 graden binnen. Ankers op Grid: 70, 95 en
// 122 (accu-omvormer) - telkens minstens 25 graden uit elkaar.
// Telefoon op een site zonder eigen PV aan de accu-omvormer (Erdotex): de
// daken staan onder elkaar op de plek van de PV-bol. Dak 1 loopt recht naar
// Grid (180 graden), Dak 2 komt linksonder binnen (135); de accu-omvormer
// houdt 90. Ankers 45 graden uit elkaar, geen kruising met de hub-lijn.
export const COMPACT_ZONDER_PV_POS = [
  { cx: 310, cy: 72 },
  { cx: 310, cy: 175 },
];
export const LIJN_COMPACT_ZONDER_PV = [
  { angA: 0, angB: 180, dA: 0, dB: 0 },
  { angA: 0, angB: 135, dA: 50, dB: 50 },
];
// Desktop op een site zonder eigen PV aan de accu-omvormer (Erdotex, Bram
// 15-10-2026: "de twee daken onder elkaar met een eigen lijn naar GRID"):
// geen vierde kolom, maar de daken in de linkerkolom op de plek van de PV.
// Dak 1 neemt rij 1 over (cy 75, gelijk met Grid) en loopt recht naar Grid,
// zoals PV vroeger de bovenste rij deelde met Grid. Dak 2 staat er direct
// onder, met dezelfde pilmaat (pil eindigt op cx, 280 x 90) en 20 px tussen
// de pillen; zijn lijn komt linksonder op Grid binnen (135). De accu-omvormer
// houdt zijn oorspronkelijke anker (90). Ankers 180/135/90: 45 graden uit
// elkaar, geen kruisingen, en het raster van het origineel (800 breed) blijft.
export const DESKTOP_ZONDER_PV_POS = [
  { cx: 310, cy: 75 },
  { cx: 310, cy: 185 },
];
export const LIJN_DESKTOP_ZONDER_PV = [
  { angA: 0, angB: 180, dA: 0, dB: 0 },
  { angA: 0, angB: 135, dA: 60, dB: 60 },
];
const LIJN = [
  { angA: 225, angB: 70, dA: 110, dB: 60 },
  { angA: 180, angB: 95, dA: 100, dB: 120 },
];

function padViaBaan(dak: Circle, grid: Circle, g: { angA: number; angB: number; dA: number; dB: number }): string {
  const p0 = op(dak, g.angA);
  const baanX = dak.cx - 70;
  const r = p0[0] - baanX;               // kwartboog-straal
  const k = 0.5523 * r;                  // cirkelbenadering met een cubic
  const boogEind: [number, number] = [baanX, p0[1] - r];
  const baanTop = 300;                   // bocht begint naast Verbruik; c1 ver boven houdt hem vloeiend (C1) en vrij van de pil
  const p3 = op(grid, g.angB);
  const c2 = op(grid, g.angB, g.dB);
  return `M ${rnd(p0[0])} ${rnd(p0[1])} `
    + `C ${rnd(p0[0] - k)} ${rnd(p0[1])}, ${rnd(baanX)} ${rnd(boogEind[1] + k)}, ${rnd(baanX)} ${rnd(boogEind[1])} `
    + `L ${rnd(baanX)} ${rnd(baanTop)} `
    + `C ${rnd(baanX)} ${rnd(baanTop - g.dA)}, ${rnd(c2[0])} ${rnd(c2[1])}, ${rnd(p3[0])} ${rnd(p3[1])}`;
}

interface Omvormer { key: string; naam: string; powerW: number | null; stale: boolean }
export interface Dak {
  key: string;
  label: string;
  bol: Circle;
  powerW: number;
  todayKwh: number;
  nOk: number;
  nTotal: number;
  stale: boolean;
  omvormers: Omvormer[];
}

// Meer bronnen dan plekken (bv. drie daken): één verzamelbol op de eerste
// plek, met in de pil een stip per dak in plaats van per omvormer. Zo valt er
// nooit een bron stil weg en blijft de compositie gelijk, wat het aantal
// daken ook wordt. De "!"-telling blijft per omvormer.
function verzamel(daken: Dak[], bol: Circle): Dak {
  const nOk = daken.reduce((n, d) => n + d.nOk, 0);
  return {
    key: 'remote-pv-totaal',
    label: `Daken (${daken.length})`,
    bol,
    powerW: daken.reduce((w, d) => w + d.powerW, 0),
    todayKwh: daken.reduce((k, d) => k + d.todayKwh, 0),
    nOk,
    nTotal: daken.reduce((n, d) => n + d.nTotal, 0),
    stale: nOk === 0,
    omvormers: daken.map((d) => ({ key: d.key, naam: d.label, powerW: d.stale ? null : d.powerW, stale: d.stale })),
  };
}

export function layoutDaken(rpv: RemotePv, pos: { cx: number; cy: number }[] = DESKTOP_POS): Dak[] {
  // Ook een bron zonder omvormerlijst (meet-Pi nog nooit online) wordt een dak, offline.
  const bronnen = rpv.sources ?? [];
  const plekken = bronnen.length > pos.length ? bronnen.map(() => pos[0]) : pos;
  const daken = bronnen.map((src, i) => {
    const omvormers: Omvormer[] = (src.inverters ?? []).map((inv, j) => {
      const stale = !src.ok || inv.stale || !inv.ok;
      return {
        key: `${src.name}-${inv.id ?? j}`,
        naam: inv.name?.split(' ')[0] ?? `WR${inv.id ?? j + 1}`,
        powerW: stale ? null : (inv.power_w ?? null),
        stale,
      };
    });
    const nOk = omvormers.filter((o) => !o.stale).length;
    return {
      key: src.name,
      label: src.label ?? src.name,
      bol: { ...plekken[i], r: R },
      powerW: src.power_w ?? omvormers.reduce((s, o) => s + (o.powerW ?? 0), 0),
      todayKwh: src.today_kwh ?? (src.inverters ?? []).reduce((s, inv) => s + (inv.today_kwh ?? 0), 0),
      nOk,
      nTotal: omvormers.length,
      stale: nOk === 0,
      omvormers,
    };
  });
  return daken.length > pos.length ? [verzamel(daken, { ...pos[0], r: R })] : daken;
}

// Zelfde rekenwerk als anchor/outward/flowBezier in FlowSVG.
const rnd = (n: number) => n.toFixed(1);
const rad = (d: number) => (d * Math.PI) / 180;
const op = (c: Circle, a: number, extra = 0): [number, number] =>
  [c.cx + (c.r + extra) * Math.cos(rad(a)), c.cy + (c.r + extra) * Math.sin(rad(a))];
function bezier(a: Circle, b: Circle, angA: number, angB: number, dA: number, dB: number): string {
  const p0 = op(a, angA);
  const p3 = op(b, angB);
  const c1 = op(a, angA, dA);
  const c2 = op(b, angB, dB);
  return `M ${rnd(p0[0])} ${rnd(p0[1])} C ${rnd(c1[0])} ${rnd(c1[1])}, ${rnd(c2[0])} ${rnd(c2[1])}, ${rnd(p3[0])} ${rnd(p3[1])}`;
}

export function DakLijnen({ daken, grid, geo = LIJN }: { daken: Dak[]; grid: Circle; geo?: typeof LIJN }) {
  return (
    <g className="remote-dak-lijnen">
      {daken.map((d, i) => {
        const g = geo[i];
        const pad = geo === LIJN && i === 1 ? padViaBaan(d.bol, grid, g) : bezier(d.bol, grid, g.angA, g.angB, g.dA, g.dB);
        const actief = !d.stale && d.powerW >= 100;
        return actief ? (
          <g key={d.key}>
            <path d={pad} stroke="#fbbf24" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.30" />
            <path d={pad} stroke="#f59e0b" strokeWidth="2.2" fill="none" strokeLinecap="round" strokeDasharray="9 13" markerEnd="url(#arr-pv)">
              <animate attributeName="stroke-dashoffset" from="0" to="-22" dur="1.5s" repeatCount="indefinite" />
            </path>
          </g>
        ) : (
          <path key={d.key} d={pad} stroke="#cbd5e1" strokeWidth="1.4" fill="none" strokeDasharray="3 5" opacity="0.6" />
        );
      })}
    </g>
  );
}

/** Pil achter de dakbol, zelfde maat en verloop als de PV-pil (rechts: pil begint op cx). */
export function DakPillen({ daken, links = false }: { daken: Dak[]; links?: boolean }) {
  return (
    <g className="remote-dak-pillen">
      {daken.map((d) => {
        // links: pil EINDIGT op cx (zoals PV en Batterij); anders begint hij op cx.
        const x = links ? d.bol.cx - 280 : d.bol.cx;
        const top = d.bol.cy - 45;
        const tx = links ? d.bol.cx - 161 : x + 161;
        const stap = d.omvormers.length > 1 ? Math.min(40, 170 / (d.omvormers.length - 1)) : 0;
        const x0 = tx - (stap * (d.omvormers.length - 1)) / 2;
        return (
          <g key={d.key}>
            <rect x={x} y={top} width="280" height="90" rx="45" fill={links ? 'url(#g-pv)' : 'url(#g-pv-r)'} stroke="#fbbf24" strokeWidth="0.7" />
            <text x={tx} y={top + 30} textAnchor="middle" fill="#1f2937" fontWeight="600" fontSize="13">
              <title>{d.label}</title>
              {d.label.length > 24 ? `${d.label.slice(0, 23)}…` : d.label}
            </text>
            <text x={tx} y={top + 47} textAnchor="middle" fill="#6b7280" fontSize="11">
              {d.stale ? 'Vandaag —' : `Vandaag ${fmtKwh(d.todayKwh)}`}
            </text>
            {d.omvormers.map((o, j) => {
              const cx = x0 + j * stap;
              return (
                <g key={o.key}>
                  <title>{`${d.label} · ${o.naam}: ${o.stale ? 'geen actuele meting' : fmtKw(o.powerW ?? 0)}`}</title>
                  <circle
                    cx={cx} cy={top + 63} r="6"
                    fill={o.stale ? '#fff' : ((o.powerW ?? 0) >= 100 ? '#fde68a' : '#fff')}
                    stroke={o.stale ? '#cbd5e1' : '#f59e0b'} strokeWidth="1.2"
                    strokeDasharray={o.stale ? '2.5 1.5' : undefined}
                  />
                  {stap >= 24 && (
                    <text x={cx} y={top + 81} textAnchor="middle" fontSize="9.5" fontWeight="600"
                      fill={o.stale ? '#9ca3af' : '#374151'}
                    >{o.stale ? '—' : fmtKw(o.powerW ?? 0).replace(' kW', '')}</text>
                  )}
                </g>
              );
            })}
          </g>
        );
      })}
    </g>
  );
}

/** Dakbol: zelfde opbouw als de PV-bol (wit, amber rand, zon, kW eronder). */
export function DakBollen({ daken, label = false }: { daken: Dak[]; label?: boolean }) {
  return (
    <g className="remote-dak-bollen">
      {daken.map((d) => {
        const { cx, cy, r } = d.bol;
        return (
          <g key={d.key}>
            <title>{`${d.label}: ${d.nOk} van ${d.nTotal} omvormers met actuele meting`}</title>
            <circle cx={cx} cy={cy} r={r} fill="#fff"
              stroke={d.stale ? '#cbd5e1' : '#fbbf24'} strokeWidth="1.4"
              strokeDasharray={d.stale ? '5 3' : undefined}
              filter="url(#circle-shadow)" />
            <SunIcon cx={cx} cy={cy - 10} color={d.stale ? '#94a3b8' : '#f59e0b'} />
            <text x={cx} y={cy + 20} textAnchor="middle" fill="#1f2937" fontWeight="700" fontSize="13">
              {d.stale ? '—' : fmtKwBol(d.powerW)}
            </text>
            {label && (
              <text x={cx} y={cy + r + 14} textAnchor="middle" fill="#6b7280" fontSize="10">{d.nTotal > 0 ? `${d.label} · ${d.nOk}/${d.nTotal}` : `${d.label} · —`}</text>
            )}
            {(d.nOk < d.nTotal || d.stale) && (
              <g>
                <circle cx={cx + 24} cy={cy - 22} r="9" fill="#f59e0b" stroke="#fff" strokeWidth="1.5" />
                <text x={cx + 24} y={cy - 18} textAnchor="middle" fill="#fff" fontWeight="700" fontSize="12">!</text>
              </g>
            )}
          </g>
        );
      })}
    </g>
  );
}
